import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signUp } from "../../services/operations/authAPI";
import AuthLeftPanel from "../../components/Auth/AuthLeftPanel";
import AuthRightPanel from "../../components/Auth/AuthRightPanel";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    class: "",
    password: "",
    confirmPassword: "",
    role: "student",
  });

  const [emailValid, setEmailValid] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Allow only @mnrtechnologies.com and @gmail.com
  const validateEmail = (email) => {
    const regex =
      /^[a-zA-Z0-9._%+-]+@(mnrtechnologies\.com|gmail\.com)$/i;

    return regex.test(email.trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = form.email.trim();

    // Validate required fields
    if (
      !form.name.trim() ||
      !normalizedEmail ||
      !form.class ||
      !form.password ||
      !form.confirmPassword
    ) {
      toast.error("All fields are required");
      return;
    }

    // Validate email domain
    if (!validateEmail(normalizedEmail)) {
      setEmailValid(false);
      toast.error(
        "Email must end with @mnrtechnologies.com or @gmail.com"
      );
      return;
    }

    // Validate password match
    if (form.password !== form.confirmPassword) {
      toast.error("Password and Confirm Password do not match");
      return;
    }

    // Always register as a student
    const signupData = {
      name: form.name.trim(),
      email: normalizedEmail,
      password: form.password,
      confirmPassword: form.confirmPassword,
      role: "student",
      class: form.class,
    };

    setSubmitting(true);
    await dispatch(signUp(signupData, navigate, "/login"));
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex bg-white text-[#04254d] flex-col lg:flex-row">
      <AuthLeftPanel />

      <AuthRightPanel>
        <h2 className="text-2xl font-semibold mb-6 text-center">
          Create Your Account
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full Name */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Full Name
            </label>

            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
              required
              autoComplete="name"
              placeholder="Enter your full name"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d] focus:outline-none"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Email
            </label>

            <input
              type="email"
              value={form.email}
              onChange={(e) => {
                const value = e.target.value;

                setForm({ ...form, email: value });
                setEmailValid(
                  value.trim() === "" || validateEmail(value)
                );
              }}
              required
              autoComplete="email"
              placeholder="Enter your email address"
              aria-invalid={!emailValid}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#04254d] focus:outline-none ${
                emailValid ? "border-gray-300" : "border-red-500"
              }`}
            />

            {!emailValid && (
              <p className="text-sm text-red-600 mt-1">
                Use an @mnrtechnologies.com or @gmail.com email
                address.
              </p>
            )}

            <p className="text-xs text-gray-500 mt-1">
              Allowed domains: @mnrtechnologies.com and @gmail.com
            </p>
          </div>

          {/* Class Selection */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Class
            </label>

            <select
              value={form.class}
              onChange={(e) =>
                setForm({ ...form, class: e.target.value })
              }
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d] focus:outline-none"
            >
              <option value="" disabled>
                Select your class
              </option>

              {[6, 7, 8, 9, 10].map((cls) => (
                <option key={cls} value={cls}>
                  Class {cls}
                </option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Password
            </label>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                required
                autoComplete="new-password"
                placeholder="Enter your password"
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-3 flex items-center text-gray-600 hover:text-gray-800"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Confirm Password
            </label>

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({
                    ...form,
                    confirmPassword: e.target.value,
                  })
                }
                required
                autoComplete="new-password"
                placeholder="Confirm your password"
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
                className="absolute inset-y-0 right-3 flex items-center text-gray-600 hover:text-gray-800"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-md font-semibold transition-colors bg-[#04254d] text-white hover:bg-[#0c1c5c] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Create Account
          </button>
        </form>

        <p className="text-sm text-center mt-4 text-gray-600">
          Already have an account?{" "}
          <a
            href="/login"
            className="text-[#04254d] font-semibold hover:underline"
          >
            Login
          </a>
        </p>
      </AuthRightPanel>
    </div>
  );
}
